import { NavigationGuard } from 'vue-router';
// import { RouteNames as WelcomeRouteNames } from '../../components/welcome/_routes';

import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { PluginObject } from 'vue/types/umd';

import { RouteNames } from '@/routes';
import { LocalStorageKeys } from './cache';
import * as lantern from '@lantern/lantern-sdk';

import { Mutex } from './mutex';

import { router } from '../main';
import { InternalAxiosRequestConfig } from 'axios';

let hasAuthenticatedBefore = false;

const SERVICE_HOST = process.env.VUE_APP_SERVICE_HOST;
const TOKEN_REFRESH_INTERVAL = parseInt(process.env.VUE_APP_TOKEN_REFRESH_INTERVAL!, 10) || 50;

export function logout() {
    hasAuthenticatedBefore = false;
}

export const authenticatedGuard: NavigationGuard = async (to, from, next) => {
    try {
        const authService = Auth.instance!;

        const fn = async () => {
            if (authService.error) {
                next({ replace: true, name: RouteNames.error, params: { title: 'Authentication error', error: authService.error, to: to.fullPath } });
                return;
            }

            if (authService.isAuthenticated) {
                next();
                return;
            }

            const loginWithRedirect = () => {
                const url = new URL('/v1/oauth2/authorize', SERVICE_HOST);
                const redirectUri = `${location.protocol}//${location.hostname}${location.port ? `:${location.port}` : ''}`;
                url.searchParams.append('redirectUri', redirectUri);
                window.location.replace(url.href);
            };

            loginWithRedirect();
        };

        if (!authService.loading) {
            await fn();
            return;
        }

        const unwatch = authService.$watch('loading', (loading: any) => {
            console.log('loading');
            if (loading === false) {
                fn()
                    .catch(error => {
                        console.log(error);
                        next(false);
                    })
                    .finally(unwatch);
            }
        });
    } catch (error) {
        console.log(error);
        console.log('next()');
        next(false);
    }
};

const DEFAULT_REDIRECT_CALLBACK = () => window.history.replaceState({}, document.title, window.location.pathname);

export interface CanvasOAuthOptions {
    onRedirectCallback?: (appState?: any) => void;
}

interface AuthConstructorOptions {
    onRedirectCallback: (appState?: any) => void;
}

@Component
export class Auth extends Vue {
    private static _instance?: Auth;
    private static options: AuthConstructorOptions;

    public isAuthenticated = false;
    public loading = true;
    public error = '';
    private accessTokenExpire = true;
    private timer: any;

    public static get instance() {
        return Auth._instance;
    }

    public static CanvasOAuthPlugin: PluginObject<CanvasOAuthOptions> = {
        install(vue: Vue.VueConstructor<Vue>, options: CanvasOAuthOptions = {}) {
            Auth.options = {
                onRedirectCallback: options.onRedirectCallback || DEFAULT_REDIRECT_CALLBACK,
                ...options
            };
            Auth._instance = new Auth();
            (<any>vue).prototype.$auth = Auth._instance;
        }
    };

    async created() {
        // Attach token refresh logic to lantern sdk error interceptor
        this.$lantern.setTokenRefreshFn(this.refreshToken, this.waitForRefresh);

        console.log(`created`);

        try {
            console.log(`Checking url query...`);
            if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
                const appState = await this.handleRedirectCallback();
                Auth.options.onRedirectCallback(appState);

            }
        } catch (err: any) {
            this.error = err;
            this.loading = false;
            // Propagate error back to authentication guard watching this.loading
            return;
        }

        console.log(`Checking for local token`);

        const token = window.localStorage.getItem(LocalStorageKeys.AuthToken);
        const permissionToken = window.localStorage.getItem(LocalStorageKeys.PermissionToken);
        const refreshToken = window.localStorage.getItem(LocalStorageKeys.RefreshToken);
        const pollForToken = window.localStorage.getItem(LocalStorageKeys.PollForToken) === 'true';

        if (!token || !permissionToken || !refreshToken) {
            console.log(`No local token`);
            this.loading = false;
            return;
        }

        this.$lantern.token = token;
        this.$lantern.permissionToken = permissionToken;

        // getAccount to long - we will call it on UI to avoid net error on server
        // try
        if (pollForToken === true) await this.getTokenDataOnClient();

        try {

            console.log('Authenticating...');
            const response = await this.$lantern.endpoints.User.get();
            this.$cache.user.next(response.data);

            const { isAdmin } = (await this.$lantern.endpoints.User.isAdmin()).data;
            this.$cache.isAdmin.next(isAdmin);

            this.isAuthenticated = true;
            this.loading = false;
        } catch (err: any) {
            this.error = err;
            console.log(err);
            console.log(err.response);
            console.log(err.isAxiosError);
            // Handle when request fails and token refresh fails
            if (err.isAxiosError && err.response?.status === 401) {
                console.log('Tokens are invalid/expired');
                window.localStorage.clear();
                location.reload();
                return;
            }
            // Propagate error back to authentication guard watching this.loading
            this.loading = false;
        }

        this.autoTokenRefresh();
    }

    private async timeout(ms: number) {
        return new Promise((resolve, reject) => setTimeout(resolve, ms));
    }

    private async getTokenDataOnClient() {

        const timer = setTimeout(() => {
            throw new Error('Cannot get permissionToken!');
        }, 4000);

        let permissionToken: string | undefined;
        while (!permissionToken) {
            // eslint-disable-next-line no-await-in-loop
            await this.timeout(500);
            try {
                // eslint-disable-next-line no-await-in-loop
                const responce = await this.$lantern.endpoints.Auth.checkPermissionToken();
                permissionToken = responce.data.permission_token;
            } catch (error) {
                console.log(`get permissionToken attempt`);
                console.log(error);
            }
        }

        if (permissionToken) {
            localStorage.setItem(LocalStorageKeys.PollForToken, 'false');
            localStorage.setItem(LocalStorageKeys.PermissionToken, permissionToken);
            this.$lantern.permissionToken = permissionToken;
            clearTimeout(timer);
        }
    }

    private mutex?: Mutex<{ accessToken: string; permissionToken: string }>;

    private async refreshToken() {
        // debugger;
        console.log(`refreshToken()`);

        if (this.mutex) {
            // Token refresh in progress, waiting for token
            // eslint-disable-next-line no-return-await
            return await this.mutex.await();
        }

        // Start token refresh
        this.mutex = new Mutex();
        try {
            console.log('Refreshing token...');
            const refreshToken = window.localStorage.getItem(LocalStorageKeys.RefreshToken);
            if (!refreshToken) throw new Error('Cannot refresh access_token, no refresh_token');

            const response = await this.$lantern.endpoints.Auth.refreshCanvasOAuthToken({ refreshToken });
            const accessToken = response.data.access_token;
            const permissionToken = response.data.permission_token;
            const pollForToken = response.data.pollForToken;

            if (!accessToken) throw new Error('access_token is undefined');
            if (!permissionToken) throw new Error('permissionToken is undefined');
            if (pollForToken === undefined) throw new Error('pollForToken is undefined');

            const tokens = { accessToken, permissionToken, pollForToken };
            this.setTokens(tokens);

            this.mutex.ready(tokens);
            delete this.mutex;

            // Start auto refresh once new token is retrieved
            this.autoTokenRefresh();

            return tokens;

        } catch (error) {
            console.log('token refreshed failed.', error);
            localStorage.removeItem(LocalStorageKeys.RefreshToken);
            localStorage.removeItem(LocalStorageKeys.TokenExpirey);
            localStorage.removeItem(LocalStorageKeys.PermissionToken);
            localStorage.removeItem(LocalStorageKeys.AuthToken);

            throw error;
        }

    }

    private async waitForRefresh(config: InternalAxiosRequestConfig) {
        if (this.mutex) {
            console.log('waiting for refresh');
            // Token refresh in progress, waiting for token
            // eslint-disable-next-line no-return-await
            await this.mutex.await();
        }
        return config;
    }

    private async setTokens(tokens: { accessToken: string; permissionToken: string; pollForToken?: string }) {
        console.log('setTokens');
        const { accessToken, permissionToken, pollForToken } = tokens;
        localStorage.setItem(LocalStorageKeys.PermissionToken, permissionToken);
        localStorage.setItem(LocalStorageKeys.AuthToken, accessToken);
        if (pollForToken) {
            localStorage.setItem(LocalStorageKeys.PollForToken, pollForToken);
        }

        this.$lantern.token = accessToken;
        this.$lantern.permissionToken = permissionToken;

        const now = new Date().getTime();
        const expiryDate = new Date(now + (TOKEN_REFRESH_INTERVAL * 60 * 1000));
        localStorage.setItem(LocalStorageKeys.TokenExpirey, expiryDate.toISOString());
    }

    private async handleRedirectCallback() {
        console.log('handleRedirectCallback');
        const url = new URL(window.location.href);

        const code = url.searchParams.get('code');
        const state = url.searchParams.get('state');

        if (!code) throw new Error('code missing.');
        if (!state) throw new Error('state missing.');

        const response = await this.$lantern.endpoints.Auth.verifyCanvasOAuthToken({ code, state });
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { access_token, refresh_token, permission_token, pollForToken } = response.data;
        if (!access_token) throw new Error('access_token is undefined');
        if (!refresh_token) throw new Error('refresh_token is undefined');
        if (!permission_token) throw new Error('permission_token is undefined');

        this.$lantern.token = access_token;
        this.$lantern.permissionToken = permission_token;
    
        this.setTokens({ accessToken: access_token, permissionToken: permission_token, pollForToken: pollForToken === true ? 'true' : 'false' });

        window.localStorage.setItem(LocalStorageKeys.RefreshToken, refresh_token);
        console.log('handleRedirectCallback complete');
        return { access_token, response: response.data };

    }

    private tokenRefreshTimeout: number | null = null;

    private async autoTokenRefresh() {
        console.log(`autoTokenRefresh`);

        try {
            const dateString = localStorage.getItem(LocalStorageKeys.TokenExpirey);
            const expiryDate = dateString ? new Date(dateString) : new Date();
            let autoRefreshDelay = expiryDate.getTime() - new Date().getTime();
            if (autoRefreshDelay < 0) autoRefreshDelay = 0;

            const attemptTokenRefresh = async () => {
                try {
                    await this.refreshToken();
                } catch (err) {
                    console.log(err);
                }
            };
            this.tokenRefreshTimeout = window.setTimeout(attemptTokenRefresh, autoRefreshDelay);
            console.log(`autoTokenRefresh finished`);
        } catch (err) {
            console.warn('Auto token refresh failed.');
        }
    }

    beforeDestroy() {
        if (this.tokenRefreshTimeout) clearTimeout(this.tokenRefreshTimeout);
    }
}
